import { Button, FormControl, FormLabel, Heading, Icon, Input, Select, Stack, Text, useToast } from "@chakra-ui/react";
import { useState } from "react";
import { FaUserPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { POST } from "../../../utils/ApiRequestProvider";

interface CreateUserForm {
    email: string;
    fullName: string;
    role: 'dataentry' | 'supervisor' | 'client' | 'management';
    orgName: string;
    entityName: string;
}

const initial: CreateUserForm = {
    email: '',
    fullName: '',
    role: 'client',
    orgName: '',
    entityName: '',
};

export default function CreateUser() {
    const toast = useToast();
    const navigate = useNavigate();
    const [form, setForm] = useState<CreateUserForm>(initial);
    const [saving, setSaving] = useState(false);

    const update = (field: keyof CreateUserForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setForm((p) => ({ ...p, [field]: e.target.value as any }));

    const submit = async () => {
        if (!form.email.trim() || !form.fullName.trim()) {
            toast({ title: 'Email and name are required', status: 'warning', duration: 1500, position: 'top-right' });
            return;
        }
        setSaving(true);
        try {
            const res = await POST('/api/users', form);
            if (res.status === 'success') {
                toast({ title: 'User created', description: 'Activation email sent.', status: 'success', duration: 1500, position: 'top-right' });
                setForm(initial);
                navigate('/management/users');
            } else {
                toast({ title: res.message || 'Could not create user', status: 'error', duration: 2000, position: 'top-right' });
            }
        } catch {
            toast({ title: 'Could not create user', status: 'error', duration: 2000, position: 'top-right' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack w={'full'} justify={'center'} align={'center'} spacing={6}>
            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4}>
                <Stack direction={'row'}>
                    <Icon as={FaUserPlus} fontSize={'5xl'} color={'primary.400'} />
                    <Stack spacing={1}>
                        <Heading fontSize={'xl'}>Create User</Heading>
                        <Text fontSize={'sm'}>Only management can issue new user IDs.</Text>
                    </Stack>
                </Stack>
            </Stack>
            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4} maxW={'600px'}>
                <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input type={'email'} value={form.email} onChange={update('email')} placeholder={'user@example.com'} />
                </FormControl>
                <FormControl isRequired>
                    <FormLabel>Full name</FormLabel>
                    <Input value={form.fullName} onChange={update('fullName')} />
                </FormControl>
                <FormControl isRequired>
                    <FormLabel>Role</FormLabel>
                    <Select value={form.role} onChange={update('role')}>
                        <option value={'dataentry'}>Data Entry</option>
                        <option value={'supervisor'}>Supervisor</option>
                        <option value={'client'}>Client</option>
                        <option value={'management'}>Management</option>
                    </Select>
                </FormControl>
                <FormControl>
                    <FormLabel>Organization</FormLabel>
                    <Input value={form.orgName} onChange={update('orgName')} />
                </FormControl>
                <FormControl>
                    <FormLabel>Entity</FormLabel>
                    <Input value={form.entityName} onChange={update('entityName')} />
                </FormControl>
                <Stack direction={'row'} justify={'flex-end'}>
                    <Button variant={'ghost'} onClick={() => navigate(-1)}>Cancel</Button>
                    <Button colorScheme={'primary'} onClick={submit} isLoading={saving}>Create & send invite</Button>
                </Stack>
            </Stack>
        </Stack>
    );
}
